import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronDown, Loader2, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { phoneCountries } from "@/data/phone-countries";
import { PhoneCountry } from "@/data/types";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { parseAndValidatePhone } from "@/security/phone";
import { doc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/firebase";
import { Logo } from "@/components/shared/Logo";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";
import { updateProfile } from "firebase/auth";
import { toast } from "sonner";
import { ReferralInviteCard } from "@/components/auth/ReferralInviteCard";
import { sendVerificationEmail } from "@/lib/sendVerificationEmail";
import {
  getSignupReferralCode,
  clearSignupReferralCode,
  notifySignupReferralStorageChanged,
} from "@/lib/signupReferralStorage";
import { getPhoneOtpSentMessage } from "@/lib/phoneOtpCopy";

export default function SignUp() {
  const navigate = useNavigate();
  const {
    signUpWithEmail,
    signInWithPhone,
    confirmPhoneCode,
    error,
    clearError,
    recaptchaContainerId,
  } = useUserAuth();

  const [showEmailSignUp, setShowEmailSignUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"number" | "code" | "name">(
    "number"
  );
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(
    phoneCountries[0]
  );
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  const [emailForm, setEmailForm] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });
  const [emailPhoneCountry, setEmailPhoneCountry] = useState<PhoneCountry>(
    phoneCountries[0]
  );
  const [emailCountryPickerOpen, setEmailCountryPickerOpen] = useState(false);
  const [phoneForm, setPhoneForm] = useState({
    phone: "",
    code: "",
    /** الاسم الكامل بعد التحقق من الرمز (مسار الهاتف فقط) */
    fullName: "",
  });
  /** Optional; sent once via submitReferralCode after account is created. */
  const [referralInviteCode, setReferralInviteCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phoneSentHint, setPhoneSentHint] = useState<string | null>(null);
  const [isResendingCode, setIsResendingCode] = useState(false);

  useEffect(() => {
    const syncFromStorage = () => {
      setReferralInviteCode(getSignupReferralCode());
    };
    syncFromStorage();
    const onVisible = () => {
      if (document.visibilityState === "visible") syncFromStorage();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("signup-referral-storage", syncFromStorage);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("signup-referral-storage", syncFromStorage);
    };
  }, []);

  const trySubmitReferralAfterSignup = async () => {
    const raw = (
      referralInviteCode.trim() || getSignupReferralCode()
    ).toUpperCase();
    if (raw.length < 3) return;
    const fn = httpsCallable(functions, "submitReferralCode");
    let lastErr: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 500));
      }
      try {
        await fn({ code: raw });
        clearSignupReferralCode();
        notifySignupReferralStorageChanged();
        setReferralInviteCode("");
        return;
      } catch (e) {
        lastErr = e;
      }
    }
    const msg =
      lastErr &&
      typeof lastErr === "object" &&
      "message" in lastErr &&
      typeof (lastErr as { message: string }).message === "string"
        ? (lastErr as { message: string }).message
        : "خطأ غير معروف";
    console.error("submitReferralCode failed after retries", lastErr);
    toast.warning("لم يُربط كود الدعوة", {
      description:
        msg.includes("not-found") || msg.includes("لم يُعثر")
          ? "تأكد أن صاحب الكود فتح التطبيق مرة، ثم أدخل الكود من حسابك في الإعدادات."
          : msg,
      duration: 8000,
    });
  };

  /** لا نكتب referralCode من العميل بعد إنشاء المستند (قواعد Firestore تمنع تعديله)؛ الدالة تضبطه في الخادم. */
  const ensureReferralCodeInFirestore = async () => {
    try {
      const fn = httpsCallable(functions, "ensureReferralCode");
      await fn();
    } catch {
      // non-blocking — useUserProfile قد يضبطه لاحقاً
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const newErrors: Record<string, string> = {};
    const nameTrim = emailForm.displayName.trim();
    if (!nameTrim) newErrors.displayName = "الاسم مطلوب";
    else if (nameTrim.length < 2)
      newErrors.displayName = "الاسم حرفان على الأقل";
    if (!emailForm.email.trim()) newErrors.email = "البريد الإلكتروني مطلوب";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailForm.email))
      newErrors.email = "البريد الإلكتروني غير صحيح";
    if (!emailForm.password) newErrors.password = "كلمة المرور مطلوبة";
    else if (emailForm.password.length < 6)
      newErrors.password = "كلمة المرور 6 أحرف على الأقل";
    if (emailForm.password !== emailForm.confirmPassword)
      newErrors.confirmPassword = "كلمة المرور غير متطابقة";
    const phoneValidation = parseAndValidatePhone(
      emailForm.phone,
      emailPhoneCountry.code,
      emailPhoneCountry.dialCode
    );
    if (!phoneValidation.isValid) {
      newErrors.phone =
        phoneValidation.error || "رقم الهاتف غير صحيح";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    const ok = await signUpWithEmail(emailForm.email, emailForm.password);
    if (ok) {
      if (auth.currentUser) {
        await trySubmitReferralAfterSignup();

        const phoneE164 =
          phoneValidation.e164 ||
          `${emailPhoneCountry.dialCode}${emailForm.phone.replace(/\D/g, "")}`;
        try {
          const uid = auth.currentUser.uid;
          const displayName = emailForm.displayName.trim();
          await updateProfile(auth.currentUser, { displayName });
          await setDoc(
            doc(db, "users", uid),
            {
              displayName,
              email: emailForm.email.trim(),
              phone: phoneE164,
              countryCode: emailPhoneCountry.code,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            { merge: true },
          );
          await ensureReferralCodeInFirestore();
        } catch (err) {
          console.error("users profile write failed", err);
        }

        try {
          // Custom verification email only (no Firebase default template fallback).
          await sendVerificationEmail({
            email: auth.currentUser.email || undefined,
            displayName: emailForm.displayName.trim() || undefined,
          });
        } catch (verificationErr) {
          console.error("verification email send failed", verificationErr);
          toast.error("تعذر إرسال رسالة التحقق", {
            description: "تحقق من الإعدادات أو أعد المحاولة من شاشة التحقق.",
          });
        }
      }
      navigate("/verify-email", { replace: true });
    }
    setIsSubmitting(false);
  };

  const handlePhoneNumberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const validation = parseAndValidatePhone(
      phoneForm.phone,
      selectedCountry.code,
      selectedCountry.dialCode
    );
    if (!validation.isValid) {
      setErrors({ phone: validation.error || "رقم الهاتف غير صحيح" });
      return;
    }
    setErrors({});
    setPhoneSentHint(null);
    const phoneNumber = validation.e164 || `${selectedCountry.dialCode}${phoneForm.phone.replace(/\D/g, "")}`;
    setIsSubmitting(true);
    const ok = await signInWithPhone(phoneNumber);
    setIsSubmitting(false);
    if (ok) {
      setShowEmailSignUp(false);
      setPhoneStep("code");
      setPhoneSentHint(getPhoneOtpSentMessage());
    }
  };

  const handleResendSmsCode = async () => {
    clearError();
    setPhoneSentHint(null);
    const validation = parseAndValidatePhone(
      phoneForm.phone,
      selectedCountry.code,
      selectedCountry.dialCode
    );
    if (!validation.isValid) {
      setErrors({ phone: validation.error || "رقم الهاتف غير صحيح" });
      setPhoneStep("number");
      return;
    }
    const phoneNumber =
      validation.e164 ||
      `${selectedCountry.dialCode}${phoneForm.phone.replace(/\D/g, "")}`;
    setIsResendingCode(true);
    const ok = await signInWithPhone(phoneNumber);
    setIsResendingCode(false);
    if (ok) {
      setPhoneSentHint(getPhoneOtpSentMessage());
    }
  };

  const handlePhoneCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (
      !phoneForm.code.trim() ||
      phoneForm.code.length < 4 ||
      phoneForm.code.length > 8
    ) {
      setErrors({ code: "أدخل رمز التحقق المرسل في الرسالة النصية (4–8 أرقام)" });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    const ok = await confirmPhoneCode(phoneForm.code.trim());
    setIsSubmitting(false);
    if (ok && auth.currentUser) {
      setPhoneStep("name");
      setPhoneForm((p) => ({ ...p, fullName: "" }));
      setErrors({});
    }
  };

  const handlePhoneNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const nameTrim = phoneForm.fullName.trim();
    const newErrors: Record<string, string> = {};
    if (!nameTrim) newErrors.fullName = "الاسم مطلوب";
    else if (nameTrim.length < 2)
      newErrors.fullName = "أدخل اسمك بالكامل (حرفان على الأقل)";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (!auth.currentUser) return;

    setIsSubmitting(true);
    const phoneValidation = parseAndValidatePhone(
      phoneForm.phone,
      selectedCountry.code,
      selectedCountry.dialCode
    );
    const phoneE164 =
      phoneValidation.e164 ||
      `${selectedCountry.dialCode}${phoneForm.phone.replace(/\D/g, "")}`;
    const displayName = nameTrim;

    try {
      await updateProfile(auth.currentUser, { displayName });
      await trySubmitReferralAfterSignup();

      const uid = auth.currentUser.uid;
      await setDoc(
        doc(db, "users", uid),
        {
          displayName,
          phone: phoneE164,
          countryCode: selectedCountry.code,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
      await ensureReferralCodeInFirestore();
      navigate("/", { replace: true });
    } catch (err) {
      console.error("users profile write failed", err);
      toast.error("تعذر حفظ بيانات الحساب", {
        description: "حاول مرة أخرى أو تحقق من الاتصال.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEmailSignUp = () => {
    setShowEmailSignUp(true);
    setErrors({});
    clearError();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir="rtl">
      <div id={recaptchaContainerId} />
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
        <div className="relative pt-12 pb-6 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg">
            <Logo size="lg" showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-foreground text-center mb-1">
            إنشاء حساب
          </h1>
          <p className="text-muted-foreground text-sm text-center">
            أدخل رقم جوالك أو أنشئ حساباً بالبريد الإلكتروني
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 pb-8">
        <Card className="max-w-md mx-auto border-2">
          <CardContent className="space-y-4 pt-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {phoneStep === "number" && (
              <div className="space-y-4">
                <form onSubmit={handlePhoneNumberSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">رقم الجوال</Label>
                    <div
                      className={cn(
                        "flex items-center h-12 rounded-xl border-2 overflow-hidden",
                        errors.phone ? "border-destructive" : "border-border"
                      )}
                      dir="ltr"
                    >
                      <Popover
                        open={countryPickerOpen}
                        onOpenChange={setCountryPickerOpen}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-2 h-full px-3 border-l border-border bg-muted/30"
                          >
                            <span className="text-lg">{selectedCountry.flag}</span>
                            <span className="text-sm font-semibold">
                              {selectedCountry.dialCode}
                            </span>
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                          <div className="max-h-[260px] overflow-y-auto">
                            {phoneCountries.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(c);
                                  setCountryPickerOpen(false);
                                }}
                                className={cn(
                                  "flex items-center gap-3 w-full px-4 py-3 text-right hover:bg-primary/10",
                                  selectedCountry.code === c.code && "bg-primary/15"
                                )}
                              >
                                <span className="text-lg">{c.flag}</span>
                                <span className="flex-1 text-sm">
                                  {c.nameAr}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {c.dialCode}
                                </span>
                                {selectedCountry.code === c.code && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Input
                        id="signup-phone"
                        type="tel"
                        placeholder="ادخل رقم الجوال"
                        value={phoneForm.phone}
                        onChange={(e) =>
                          setPhoneForm((p) => ({ ...p, phone: e.target.value }))
                        }
                        className="flex-1 border-0 focus-visible:ring-0 rounded-none"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-destructive text-xs">{errors.phone}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      سيتم إرسال رمز التحقق إلى رقمك في رسالة نصية
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin ml-2" />
                        جاري إرسال رمز التحقق برسالة نصية...
                      </>
                    ) : (
                      "متابعة"
                    )}
                  </Button>
                  <ReferralInviteCard />
                </form>

                <div className="relative flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground shrink-0">أو</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {!showEmailSignUp ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 rounded-full border-2 gap-2"
                    onClick={openEmailSignUp}
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    المتابعة بالبريد الإلكتروني
                  </Button>
                ) : null}

                {showEmailSignUp && (
                  <form onSubmit={handleEmailSubmit} className="space-y-4 pt-1">
                    <div className="space-y-2">
                      <Label htmlFor="signup-display-name">الاسم</Label>
                      <Input
                        id="signup-display-name"
                        type="text"
                        placeholder="اسمك الظاهر في التطبيق"
                        value={emailForm.displayName}
                        onChange={(e) =>
                          setEmailForm((p) => ({
                            ...p,
                            displayName: e.target.value,
                          }))
                        }
                        className={cn(
                          errors.displayName && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="name"
                      />
                      {errors.displayName && (
                        <p className="text-destructive text-xs">
                          {errors.displayName}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">البريد الإلكتروني</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="example@email.com"
                        value={emailForm.email}
                        onChange={(e) =>
                          setEmailForm((p) => ({ ...p, email: e.target.value }))
                        }
                        className={cn(
                          errors.email && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="email"
                      />
                      {errors.email && (
                        <p className="text-destructive text-xs">{errors.email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>رقم الموبايل</Label>
                      <div
                        className={cn(
                          "flex items-center h-12 rounded-xl border-2 overflow-hidden",
                          errors.phone ? "border-destructive" : "border-border"
                        )}
                        dir="ltr"
                      >
                        <Popover
                          open={emailCountryPickerOpen}
                          onOpenChange={setEmailCountryPickerOpen}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 h-full px-3 border-l border-border bg-muted/30"
                            >
                              <span className="text-lg">
                                {emailPhoneCountry.flag}
                              </span>
                              <span className="text-sm font-semibold">
                                {emailPhoneCountry.dialCode}
                              </span>
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[220px] p-0" align="start">
                            <div className="max-h-[260px] overflow-y-auto">
                              {phoneCountries.map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => {
                                    setEmailPhoneCountry(c);
                                    setEmailCountryPickerOpen(false);
                                  }}
                                  className={cn(
                                    "flex items-center gap-3 w-full px-4 py-3 text-right hover:bg-primary/10",
                                    emailPhoneCountry.code === c.code &&
                                      "bg-primary/15"
                                  )}
                                >
                                  <span className="text-lg">{c.flag}</span>
                                  <span className="flex-1 text-sm">{c.nameAr}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {c.dialCode}
                                  </span>
                                  {emailPhoneCountry.code === c.code && (
                                    <Check className="h-4 w-4 text-primary" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Input
                          type="tel"
                          placeholder={emailPhoneCountry.placeholder}
                          value={emailForm.phone}
                          onChange={(e) =>
                            setEmailForm((p) => ({ ...p, phone: e.target.value }))
                          }
                          className="flex-1 border-0 focus-visible:ring-0 rounded-none"
                          disabled={isSubmitting}
                          autoComplete="tel"
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-destructive text-xs">{errors.phone}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">كلمة المرور</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="6 أحرف على الأقل"
                        value={emailForm.password}
                        onChange={(e) =>
                          setEmailForm((p) => ({ ...p, password: e.target.value }))
                        }
                        className={cn(
                          errors.password && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                      {errors.password && (
                        <p className="text-destructive text-xs">{errors.password}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">تأكيد كلمة المرور</Label>
                      <Input
                        id="signup-confirm-password"
                        type="password"
                        placeholder="أعد إدخال كلمة المرور"
                        value={emailForm.confirmPassword}
                        onChange={(e) =>
                          setEmailForm((p) => ({
                            ...p,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className={cn(
                          errors.confirmPassword && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                      {errors.confirmPassword && (
                        <p className="text-destructive text-xs">
                          {errors.confirmPassword}
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 rounded-xl"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-5 w-5 animate-spin ml-2" />
                      ) : (
                        "إنشاء الحساب"
                      )}
                    </Button>
                    <ReferralInviteCard />
                  </form>
                )}
              </div>
            )}

            {phoneStep === "code" && (
              <form onSubmit={handlePhoneCodeSubmit} className="space-y-4">
                {phoneSentHint && (
                  <p className="text-xs text-primary font-medium">{phoneSentHint}</p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="code">أدخل رمز التحقق المرسل في الرسالة النصية</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="000000"
                    value={phoneForm.code}
                    onChange={(e) =>
                      setPhoneForm((p) => ({
                        ...p,
                        code: e.target.value.replace(/\D/g, ""),
                      }))
                    }
                    className={cn(errors.code && "border-destructive")}
                    disabled={isSubmitting}
                    autoComplete="one-time-code"
                  />
                  {errors.code && (
                    <p className="text-destructive text-xs">{errors.code}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl"
                  disabled={
                    isSubmitting ||
                    phoneForm.code.length < 4 ||
                    phoneForm.code.length > 8
                  }
                >
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin ml-2" />
                  ) : (
                    "متابعة"
                  )}
                </Button>
                <ReferralInviteCard />
                <button
                  type="button"
                  onClick={handleResendSmsCode}
                  disabled={isResendingCode || isSubmitting}
                  className="w-full text-sm text-primary hover:underline disabled:opacity-50"
                >
                  {isResendingCode
                    ? "جاري إرسال رمز التحقق برسالة نصية..."
                    : "إعادة إرسال الرمز برسالة نصية"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhoneStep("number");
                    setShowEmailSignUp(false);
                    setPhoneForm((p) => ({ ...p, code: "", fullName: "" }));
                    setPhoneSentHint(null);
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  تغيير رقم الهاتف
                </button>
              </form>
            )}

            {phoneStep === "name" && (
              <form onSubmit={handlePhoneNameSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-phone-fullname">اسمك بالكامل</Label>
                  <Input
                    id="signup-phone-fullname"
                    type="text"
                    placeholder="أدخل اسمك بالكامل كما تريد أن يظهر في حسابك"
                    value={phoneForm.fullName}
                    onChange={(e) =>
                      setPhoneForm((p) => ({ ...p, fullName: e.target.value }))
                    }
                    className={cn(
                      errors.fullName && "border-destructive",
                      "rounded-xl"
                    )}
                    disabled={isSubmitting}
                    autoComplete="name"
                    autoFocus
                  />
                  {errors.fullName && (
                    <p className="text-destructive text-xs">{errors.fullName}</p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  سيظهر هذا الاسم في صفحة حسابك وباقي أجزاء التطبيق.
                </p>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin ml-2" />
                  ) : (
                    "إكمال إنشاء الحساب"
                  )}
                </Button>
                <ReferralInviteCard />
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          لديك حساب؟{" "}
          <Link to="/login" className="text-primary font-medium hover:underline">
            تسجيل الدخول
          </Link>
        </p>
      </div>
    </div>
  );
}
